import { DIMENSION_FONT_SIZE } from '@r-core/constants';

import { type GlobalSkuLabelData } from './types';

const BASE_LABEL_VERTICAL_SPACING = 2.5;
// Mirror the sizing constants from RenderAllSkuLabelsOverhead
const LABEL_PADDING_X_BASE = 0.5;
const LABEL_PADDING_Y_BASE = 0.25;
const LABEL_CHAR_WIDTH_FACTOR = 0.6;
// Gap to leave between cross-group labels that overlap
const CROSS_GROUP_NUDGE_GAP = 0.3;
const MAX_SEPARATION_PASSES = 60;

const getLabelBox = (label: GlobalSkuLabelData, fontSizeFactor: number): { cx: number; cz: number; w: number; h: number } => {
	const fontSize = DIMENSION_FONT_SIZE * fontSizeFactor;
	const paddingX = LABEL_PADDING_X_BASE * fontSizeFactor;
	const paddingY = LABEL_PADDING_Y_BASE * fontSizeFactor;
	const w = label.text.length * fontSize * LABEL_CHAR_WIDTH_FACTOR + paddingX * 2;
	const h = fontSize + paddingY * 2;
	return { cx: label.globalX, cz: label.globalZ, w, h };
};

const aabbOverlap = (a: { cx: number; cz: number; w: number; h: number }, b: { cx: number; cz: number; w: number; h: number }): boolean => {
	return Math.abs(a.cx - b.cx) < (a.w + b.w) / 2 && Math.abs(a.cz - b.cz) < (a.h + b.h) / 2;
};

// Iterative AABB nudge separating every overlapping label pair: O(n²) per pass, n = visible labels (~≤60), capped at MAX_SEPARATION_PASSES.
const separateCrossGroupOverlaps = (labels: Array<GlobalSkuLabelData>, fontSizeFactor: number): Array<GlobalSkuLabelData> => {
	const working = labels.map((l) => ({ ...l }));

	for (let pass = 0; pass < MAX_SEPARATION_PASSES; pass++) {
		let anyMoved = false;

		for (let i = 0; i < working.length; i++) {
			for (let j = i + 1; j < working.length; j++) {
				const a = working[i];
				const b = working[j];
				const boxA = getLabelBox(a, fontSizeFactor);
				const boxB = getLabelBox(b, fontSizeFactor);
				if (!aabbOverlap(boxA, boxB)) continue;

				const needed = (boxA.h + boxB.h) / 2 + CROSS_GROUP_NUDGE_GAP;
				const delta = needed - Math.abs(a.globalZ - b.globalZ);
				const shift = delta / 2;

				if (a.globalZ <= b.globalZ) {
					working[i] = { ...a, globalZ: a.globalZ - shift };
					working[j] = { ...b, globalZ: b.globalZ + shift };
				} else {
					working[i] = { ...a, globalZ: a.globalZ + shift };
					working[j] = { ...b, globalZ: b.globalZ - shift };
				}
				anyMoved = true;
			}
		}

		if (!anyMoved) break;
	}

	return working;
};

/**
 * Groups labels by their global position and stacks them vertically in GLOBAL space.
 * Within each panel-item-group, labels stack by vertical position (same-group pass).
 * A second AABB pass then separates any labels that still overlap, including same-group siblings a cross-group nudge has pushed together.
 */
export const stackLabelsGlobally = (
	labelsByGroup: Map<string, GlobalSkuLabelData[]>,
	fontSizeFactor = 1
): Array<GlobalSkuLabelData> => {
	const result: Array<GlobalSkuLabelData> = [];

	const labelVerticalSpacing = BASE_LABEL_VERTICAL_SPACING * fontSizeFactor;

	// TOP_ONLY labels (issue #249) are pre-anchored to the panel outer edge and
	// must not be re-positioned by the intra-group stack around the group
	// centerline -- they get their own stack around their pinned anchor so a
	// group holding more than one pinned label doesn't collapse them onto the
	// same point.
	const stackAroundBase = (labels: Array<GlobalSkuLabelData>): void => {
		if (labels.length === 0) return;

		const baseGlobalX = labels[0].globalX;
		const baseGlobalZ = labels[0].globalZ;

		const sortedLabels = [...labels].sort(
			(a, b) => a.verticalPosition - b.verticalPosition
		);

		const totalHeight = (sortedLabels.length - 1) * labelVerticalSpacing;
		const startZ = baseGlobalZ - totalHeight / 2;

		sortedLabels.forEach((label, index) => {
			result.push({
				...label,
				globalX: baseGlobalX,
				globalZ: startZ + index * labelVerticalSpacing
			});
		});
	};

	labelsByGroup.forEach((groupLabels) => {
		if (groupLabels.length === 0) return;

		const pinnedLabels = groupLabels.filter((label) => label.pinPosition);
		const stackableLabels = groupLabels.filter((label) => !label.pinPosition);

		stackAroundBase(pinnedLabels);
		stackAroundBase(stackableLabels);
	});

	return separateCrossGroupOverlaps(result, fontSizeFactor);
};
